/** The activity record — one activity (`demo.Activity`), read on the records core (plan
 *  2026-10-04-console-activity-itinerary-records.md §3, §9.5, §10.3).
 *
 *  The VIEW half reads three things, each through TanStack Query:
 *
 *  - `GET /activities/{pk}/` — the detail (`activities.retrieve`), under the key the old
 *    editor and every activity write already use (`['activities', 'detail', id]`);
 *  - `GET /activities/{pk}/used_by/` — the "Where it's used" panel's rows: the first page,
 *    then one short page per relation the first page did not reach (`activities.usedBy`);
 *  - `GET /activities/{pk}/used_by/summary/` — the tours and their next departure
 *    (`activities.usedBySummary`). It touches the tour-side tables, so the page asks for it
 *    only once the panel has been on screen (never with the detail); the server remembers it
 *    10 minutes, and so does the client.
 *
 *  The EDIT half (§9.6) reads and writes four more, the activity changeset's:
 *
 *  - `GET /activities/edit/catalog/` — the static registry: every field the record shows
 *    (label, card, widget, choices, what a feed sync does to it), the effects, the re-key
 *    question (`rekey_import_matching`) and the `ConsoleCatalogEditEnabled` kill switch
 *    (`enabled`). Cached for an hour (`useActivityEditCatalog`).
 *  - `GET /activities/{pk}/edit_context/` — the per-activity facts: `baselines` (every
 *    change's `from`), `blocked`, the caller's permissions, the kill switch (`flags`), the
 *    feed verdicts (`feed`, D4) and how far a saved edit reaches the app (`app_reach`, D8).
 *  - `POST /activities/{pk}/changeset/preview/` — the plan of a changeset (the normalised
 *    diff with each value's verdict, the open question, the conflicts, the effects and the
 *    work queued after the commit); writes nothing.
 *  - `POST /activities/{pk}/changeset/` — the apply, compare-and-set per field: a 409
 *    rejects as `CatalogDriftError`, a 400 (and the field-keyed 403) as
 *    `CatalogChangesetValidationError`; the answer re-seeds the detail and the edit context.
 *
 *  Vocabulary from the spec: `ActivityRecordFieldKey` (every key the record shows),
 *  `ActivityRecordEditableFieldKey` (the editable subset) and `ActivityRecordSection` (the
 *  cards, in page order) — the registry's contract test pins to the same enums. */
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import type { RecordApplyResult, RecordChangeset, RecordPreview } from '@/features/records/editor/types';

import * as activitiesApi from './activities';
import type {
  ActivityDetail,
  ActivityUsedByList,
  ActivityUsedBySummary,
  ActivityUsedByType,
} from './activities';
import { CatalogDriftError, rekeyOptions, toCatalogChangesetError } from './catalogRecord';
import { apiGet, apiPost } from './client';
import type { components } from './types';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Every field key the activity record shows, editable or not, in card order. */
export type ActivityRecordFieldKey = components['schemas']['ActivityRecordFieldKey'];

/** The keys an `activity:<id>` change may carry. */
export type ActivityRecordEditableFieldKey = components['schemas']['ActivityRecordEditableFieldKey'];

/** The record's cards, in page order (ActivityRecordSection). */
export const ACTIVITY_RECORD_SECTIONS = [
  'about',
  'place',
  'visit',
  'contact',
  'nearby',
  'access',
  'identity',
  'technical',
] as const satisfies readonly components['schemas']['ActivityRecordSection'][];
export type ActivityRecordSection = (typeof ACTIVITY_RECORD_SECTIONS)[number];

// ---------------------------------------------------------------------------
// Query keys (all under `['activities']`, so an activity write refreshes them)
// ---------------------------------------------------------------------------

export const activityDetailKey = (id: number) => ['activities', 'detail', id] as const;
/** The panel's used-by pages: the first one (`type` undefined) and one per relation. */
export const activityWhereUsedKey = (id: number, type?: ActivityUsedByType) =>
  ['activities', 'usedBy', id, 'panel', type ?? 'all'] as const;
export const activityUsedBySummaryKey = (id: number) => ['activities', 'usedBySummary', id] as const;

/** The server remembers a summary 10 minutes (2 when the read was cancelled). */
const SUMMARY_STALE_MS = 10 * 60_000;

/** The panel's first page: enough rows for most activities' every relation. */
export const WHERE_USED_FIRST_PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useActivityDetail(
  id: number,
  opts: { retry?: (failures: number, error: unknown) => boolean } = {},
): UseQueryResult<ActivityDetail, unknown> {
  return useQuery<ActivityDetail, unknown>({
    queryKey: activityDetailKey(id),
    queryFn: () => activitiesApi.retrieve(id),
    ...(opts.retry ? { retry: opts.retry } : {}),
  });
}

/** The used-by summary — `enabled` only once the "Where it's used" panel has been seen. An
 *  unavailable summary (the database cancelled the read) is an answer, not an error. */
export function useActivityUsedBySummary(
  id: number,
  { enabled }: { enabled: boolean },
): UseQueryResult<ActivityUsedBySummary, unknown> {
  return useQuery<ActivityUsedBySummary, unknown>({
    queryKey: activityUsedBySummaryKey(id),
    queryFn: () => activitiesApi.usedBySummary(id),
    enabled,
    staleTime: SUMMARY_STALE_MS,
    retry: 1,
  });
}

/** The relations whose first rows the first page did not bring: each listed relation that
 *  has rows the page left out while it shows fewer than `first` of them. */
export function relationsToFetch(
  page: ActivityUsedByList,
  first: number,
): ActivityUsedByType[] {
  const shown = new Map<string, number>();
  for (const row of page.results) shown.set(row.type_key, (shown.get(row.type_key) ?? 0) + 1);
  return page.available_types
    .filter((t) => !t.count_only && t.count > (shown.get(t.key) ?? 0) && (shown.get(t.key) ?? 0) < first)
    .map((t) => t.key);
}

export interface WhereUsedPages {
  /** The pages, once every one the panel needs has arrived; undefined while any loads. */
  pages: ActivityUsedByList[] | undefined;
  failed: boolean;
  refetch: () => void;
}

/** The "Where it's used" panel's pages: the first page, then — for each relation it did not
 *  reach (rows type by type: a heavy activity's tours fill the first page) — that relation's
 *  first `first` rows. Nothing is asked until `enabled`. */
export function useWhereUsedPages(
  id: number,
  { enabled, first }: { enabled: boolean; first: number },
): WhereUsedPages {
  const firstQ = useQuery({
    queryKey: activityWhereUsedKey(id),
    queryFn: () => activitiesApi.usedBy(id, { pageSize: WHERE_USED_FIRST_PAGE_SIZE }),
    enabled,
  });
  const more = firstQ.data ? relationsToFetch(firstQ.data, first) : [];
  const moreQs = useQueries({
    queries: more.map((type) => ({
      queryKey: activityWhereUsedKey(id, type),
      queryFn: () => activitiesApi.usedBy(id, { type, pageSize: first }),
      enabled,
    })),
  });
  const failed = Boolean(firstQ.error) || moreQs.some((q) => q.error);
  const loaded = firstQ.data !== undefined && moreQs.every((q) => q.data !== undefined);
  // One array per set of answers (the queries' own `data` are stable between renders).
  const answered = [firstQ.dataUpdatedAt, ...moreQs.map((q) => q.dataUpdatedAt)].join(':');
  const pages = useMemo(
    () => (loaded && firstQ.data ? [firstQ.data, ...moreQs.map((q) => q.data!)] : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `answered` stands for the queries' data
    [loaded, answered],
  );
  return {
    pages,
    failed,
    refetch: () => {
      void firstQ.refetch();
      for (const q of moreQs) if (q.error) void q.refetch();
    },
  };
}

// ---------------------------------------------------------------------------
// EDIT half: the activity changeset (§9.6)
// ---------------------------------------------------------------------------

type Schemas = components['schemas'];

export type ActivityRecordCatalog = Schemas['ActivityRecordCatalog'];
export type ActivityRecordCatalogField = Schemas['ActivityRecordCatalogField'];
export type ActivityRecordCatalogOption = Schemas['ActivityRecordCatalogOption'];
export type ActivityRecordEditContext = Schemas['ActivityRecordEditContext'];
export type ActivityRecordChange = Schemas['ActivityRecordChange'];
export type ActivityRecordChangeset = Schemas['ActivityRecordChangeset'];
export type ActivityRecordPreview = Schemas['ActivityRecordPreview'];
/** The apply's answer, its `detail` in the shape the record reads (`activities.retrieve`). */
export type ActivityRecordApplyResult = Omit<Schemas['ActivityRecordApplyResult'], 'detail'> & {
  detail: ActivityDetail | null;
};

/** The change entity of the activity record. */
export const ACTIVITY_CHANGE_ENTITY = 'activity';

export { REKEY_IMPORT_MATCHING, REKEY_IMPORT_MATCHING_TO } from './catalogRecord';

/** The records core's changeset as this endpoint takes it: the activity's field changes
 *  (an activity takes no actions and no events), the description, and the re-key answer
 *  with the Custom ID it confirms (null confirms a removal). */
export function toActivityChangeset(body: RecordChangeset): ActivityRecordChangeset {
  const out: ActivityRecordChangeset = {
    changes: body.changes.flatMap((c) =>
      c.entity === ACTIVITY_CHANGE_ENTITY && c.field
        ? [
            {
              entity: ACTIVITY_CHANGE_ENTITY,
              id: c.id,
              field: c.field as ActivityRecordChange['field'],
              from: c.from,
              to: c.to,
            },
          ]
        : [],
    ),
  };
  if (body.description) out.description = body.description;
  const options = rekeyOptions(body.options);
  if (options) out.options = options;
  return out;
}

/** The automatic effect whose label is the app-reach sentence (plan §8, D8). */
export const APP_REACH_EFFECT = 'app_reach';

/** An activity plan as the ledger reads it. An activity fires no events; the work its save
 *  queues after the commit (the time zone from new coordinates) is the save's own follow-up
 *  — "Caches & syncs" — not a write to another system, so it asks no confirmation. The
 *  app-reach effect is its sentence alone: it counts no records of its own. */
export function toActivityRecordPreview(p: ActivityRecordPreview): RecordPreview {
  const effects = Object.fromEntries(
    Object.entries(p.effects).map(([code, e]) => [code, code === APP_REACH_EFFECT ? { ...e, count: 0 } : e]),
  );
  return { ...p, effects, events: [], external: [], follow_ups: p.external };
}

/** An apply result as the ledger reads it (no events, no event tag). */
export function toActivityApplyResult(r: ActivityRecordApplyResult): RecordApplyResult {
  return {
    audit_id: r.audit_id,
    audit_uuid: r.audit_uuid,
    event_tag: null,
    saved_ts: r.saved_ts,
    changes: r.changes,
    events: [],
    follow_ups: r.follow_ups,
    warnings: r.warnings,
  };
}

// ---- calls -----------------------------------------------------------------

export function fetchActivityEditCatalog(): Promise<ActivityRecordCatalog> {
  return apiGet<ActivityRecordCatalog>('/activities/edit/catalog/');
}

export function fetchActivityEditContext(id: number, signal?: AbortSignal): Promise<ActivityRecordEditContext> {
  return apiGet<ActivityRecordEditContext>(`/activities/${id}/edit_context/`, signal ? { signal } : undefined);
}

export async function previewActivityChangeset(
  id: number,
  body: ActivityRecordChangeset,
  signal?: AbortSignal,
): Promise<ActivityRecordPreview> {
  try {
    return await apiPost<ActivityRecordPreview>(`/activities/${id}/changeset/preview/`, body, signal ? { signal } : undefined);
  } catch (e) {
    throw toCatalogChangesetError(e);
  }
}

export async function applyActivityChangeset(id: number, body: ActivityRecordChangeset): Promise<ActivityRecordApplyResult> {
  try {
    return await apiPost<ActivityRecordApplyResult>(`/activities/${id}/changeset/`, body);
  } catch (e) {
    throw toCatalogChangesetError(e);
  }
}

// ---- React Query -----------------------------------------------------------

/** The catalog changes only with a deploy. */
export const ACTIVITY_EDIT_CATALOG_STALE_MS = 60 * 60_000;

export const activityRecordKeys = {
  catalog: () => ['activityRecord', 'catalog'] as const,
  editContext: (id: number) => ['activityRecord', 'editContext', id] as const,
  preview: (id: number, body: ActivityRecordChangeset) => ['activityRecord', 'preview', id, body] as const,
};

export function useActivityEditCatalog(): UseQueryResult<ActivityRecordCatalog> {
  return useQuery({
    queryKey: activityRecordKeys.catalog(),
    queryFn: fetchActivityEditCatalog,
    staleTime: ACTIVITY_EDIT_CATALOG_STALE_MS,
  });
}

export function useActivityEditContext(
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<ActivityRecordEditContext> {
  return useQuery({
    queryKey: activityRecordKeys.editContext(id),
    queryFn: ({ signal }) => fetchActivityEditContext(id, signal),
    enabled: enabled && Number.isFinite(id),
  });
}

/** The ledger's preview of a (debounced) compiled draft; `null` = nothing to preview —
 *  keyed by the body, the previous plan kept on screen while the next loads. */
export function useActivityChangesetPreview(id: number, body: RecordChangeset | null): UseQueryResult<RecordPreview> {
  const wire = body ? toActivityChangeset(body) : { changes: [] };
  return useQuery({
    queryKey: activityRecordKeys.preview(id, wire),
    queryFn: ({ signal }) => previewActivityChangeset(id, wire, signal),
    select: toActivityRecordPreview,
    enabled: body !== null && wire.changes.length > 0 && Number.isFinite(id),
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** Save. `onSaved` runs first (the draft lets go of what the save stored before the
 *  re-seeded baselines rebase it), then the edit context and the detail are re-seeded from
 *  the answer — refetched instead when the server could not re-read them — and the
 *  activities grids refresh. A 409 rejects with `CatalogDriftError` (the edit context is
 *  refetched for the rebase), a 400 / field-keyed 403 with `CatalogChangesetValidationError`. */
export function useApplyActivityChangeset(
  id: number,
  { onSaved }: { onSaved?: (result: RecordApplyResult) => void } = {},
): UseMutationResult<ActivityRecordApplyResult, unknown, RecordChangeset> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordChangeset) => applyActivityChangeset(id, toActivityChangeset(body)),
    onSuccess: (result) => {
      onSaved?.(toActivityApplyResult(result));
      if (result.edit_context) qc.setQueryData(activityRecordKeys.editContext(id), result.edit_context);
      else void qc.invalidateQueries({ queryKey: activityRecordKeys.editContext(id) });
      if (result.detail) qc.setQueryData(activityDetailKey(id), result.detail);
      // The rest under `['activities']`: the grids, the used-by pages — and the detail
      // itself when the answer could not re-read it. Not the used-by summary: it reads the
      // tour-side tables, and an edit changes no usage.
      void qc.invalidateQueries({
        queryKey: ['activities'],
        predicate: ({ queryKey: [, kind, key] }) =>
          kind !== 'usedBySummary' && !(result.detail && kind === 'detail' && key === id),
      });
    },
    onError: (e) => {
      if (e instanceof CatalogDriftError) {
        void qc.invalidateQueries({ queryKey: activityRecordKeys.editContext(id) });
      }
    },
  });
}
